// The Unitly logo system (direction 3d + 3e, see design/brand/logo-options.html):
// Wordmark: Urbanist, lowercase, a dotless i with a square dot sized and placed from real glyph metrics.
// DimensionLockup (3d): a dimension string with 45 degree ticks measuring the wordmark.
// ModularMark (3e): seven ink units on a faint 3 x 3 grid form a u; the eighth, terracotta, is being placed.
import { useEffect, useState, type CSSProperties } from "react";

export const INK = "#161513";
export const TERRACOTTA = "#E8603C";
export const BLUE = "#2348FF";

type Metrics = { stem: number; cx: number; xh: number; asc: number };
const FALLBACK: Record<number, Metrics> = {
  600: { stem: 0.13, cx: 0.11, xh: 0.5, asc: 0.72 },
  700: { stem: 0.145, cx: 0.115, xh: 0.5, asc: 0.72 },
  800: { stem: 0.16, cx: 0.12, xh: 0.5, asc: 0.72 },
};
let measured: Record<number, Metrics> | null = null;
let pending: Promise<Record<number, Metrics>> | null = null;

function measure(): Promise<Record<number, Metrics>> {
  if (measured) return Promise.resolve(measured);
  if (!pending) {
    pending = (async () => {
      try {
        await Promise.all([600, 700, 800].map((w) => document.fonts.load(`${w} 100px Urbanist`)));
        await document.fonts.ready;
        const c = document.createElement("canvas").getContext("2d");
        if (!c) return FALLBACK;
        const m: Record<number, Metrics> = {};
        for (const w of [600, 700, 800]) {
          c.font = `${w} 100px Urbanist`;
          const i = c.measureText("ı");
          const l = c.measureText("l");
          m[w] = {
            stem: (i.actualBoundingBoxRight + i.actualBoundingBoxLeft) / 100,
            cx: (i.actualBoundingBoxRight - i.actualBoundingBoxLeft) / 200,
            xh: i.actualBoundingBoxAscent / 100,
            asc: l.actualBoundingBoxAscent / 100,
          };
        }
        measured = m;
        return m;
      } catch {
        return FALLBACK;
      }
    })();
  }
  return pending;
}

function useMetrics(weight: number): Metrics {
  const [m, setM] = useState<Metrics>(measured?.[weight] ?? FALLBACK[weight]);
  useEffect(() => {
    let live = true;
    measure().then((all) => live && setM(all[weight] ?? FALLBACK[weight]));
    return () => {
      live = false;
    };
  }, [weight]);
  return m;
}

/** unitly, with the square dot: side 1.15 x stem, centred on the stem, top on the ascender line. */
export function Wordmark({ size, ink = INK, dot = TERRACOTTA, weight = 800, style }: {
  size: number; ink?: string; dot?: string; weight?: 700 | 800; style?: CSSProperties;
}) {
  const M = useMetrics(weight);
  const d = M.stem * 1.15;
  const bottom = M.asc - d;
  return (
    <span
      aria-label="unitly"
      role="img"
      style={{
        fontFamily: "Urbanist, sans-serif", fontWeight: weight, fontSize: size, lineHeight: 1,
        letterSpacing: weight === 800 ? "-0.04em" : "-0.03em", color: ink, whiteSpace: "nowrap", ...style,
      }}
    >
      un
      <span style={{ display: "inline-block", width: 0, height: 0, position: "relative", verticalAlign: "baseline" }}>
        <span style={{ position: "absolute", left: `${M.cx - d / 2}em`, bottom: `${bottom}em`, width: `${d}em`, height: `${d}em`, background: dot }} />
      </span>
      {"ı"}tly
    </span>
  );
}

/** 3d: dimension string above the wordmark. Below 24 px the string is dropped. */
export function DimensionLockup({ size, label = "UNIT.LY", ink = INK, dot = TERRACOTTA, background = "var(--paper)" }: {
  size: number; label?: string; ink?: string; dot?: string; background?: string;
}) {
  if (size < 24) return <Wordmark size={size} ink={ink} dot={dot} />;
  const t = Math.max(1, size / 64);
  const tick = (side: "left" | "right"): CSSProperties => ({
    position: "absolute", [side]: -size * 0.09, top: size * 0.22 - t / 2, width: size * 0.18, height: t * 1.4,
    background: ink, transform: "rotate(-45deg)",
  });
  const ext = (side: "left" | "right"): CSSProperties => ({ position: "absolute", [side]: 0, top: size * 0.1, width: t, height: size * 0.28, background: ink });
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: size * 0.06 }}>
      <span aria-hidden style={{ position: "relative", display: "block", height: size * 0.36 }}>
        <span style={{ position: "absolute", left: 0, right: 0, top: size * 0.22 - t / 2, height: t, background: ink }} />
        <span style={tick("left")} />
        <span style={tick("right")} />
        <span style={ext("left")} />
        <span style={ext("right")} />
        <span style={{
          position: "absolute", left: "50%", top: 0, transform: "translate(-50%,-30%)", padding: `0 ${size * 0.08}px`,
          background, font: `500 ${Math.max(9, size * 0.15)}px Inter, sans-serif`, letterSpacing: "0.06em", color: ink, whiteSpace: "nowrap",
        }}>{label}</span>
      </span>
      <Wordmark size={size} ink={ink} dot={dot} />
    </span>
  );
}

const U_CELLS: [number, number][] = [[0, 0], [0, 1], [0, 2], [1, 2], [2, 2], [2, 1], [2, 0]];

/** 3e: the modular u. animate drops the terracotta unit into place (loading state). */
export function ModularMark({ size, ink = INK, accent = TERRACOTTA, grid = true, animate = false, title }: {
  size: number; ink?: string; accent?: string; grid?: boolean; animate?: boolean; title?: string;
}) {
  return (
    <svg width={size} height={size} viewBox="-8 -8 116 116" role="img" aria-label={title ?? "Unitly"} style={{ display: "block", flex: "none" }}>
      {grid && size >= 32 && [4, 35, 66, 97].map((v) => (
        <g key={v} stroke={ink} strokeWidth={0.8} strokeOpacity={0.35} vectorEffect="non-scaling-stroke">
          <line x1={-6} y1={v} x2={106} y2={v} vectorEffect="non-scaling-stroke" />
          <line x1={v} y1={-6} x2={v} y2={106} vectorEffect="non-scaling-stroke" />
        </g>
      ))}
      {U_CELLS.map(([c, r]) => <rect key={`${c}${r}`} x={c * 31 + 7} y={r * 31 + 7} width={25} height={25} fill={ink} />)}
      <rect x={38} y={7} width={25} height={25} fill={accent} className={animate ? "unit-drop" : undefined} />
    </svg>
  );
}

/** Symbol + wordmark lockup: the 3e mark left, Urbanist 700 wordmark with an ink dot (the colour lives in the mark).
 *  Below 32 px only the mark is used. */
export function Lockup({ height = 40 }: { height?: number }) {
  if (height < 32) return <ModularMark size={height} grid={false} />;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: height * 0.25 }}>
      <ModularMark size={height} />
      <Wordmark size={height * 0.7} weight={700} dot={INK} />
    </span>
  );
}
